import React from 'react'
import { faCalendar, faLightbulb } from '@fortawesome/pro-solid-svg-icons'

import { Badge, Icon, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'

export interface SummaryDueDate {
  /** What the date is for, e.g. "Q3 estimated tax payment". */
  label: string
  /** Already formatted for display. The card never parses or formats a date
   *  itself, so there is one place deciding what a deadline looks like. */
  date: string
}

export interface SectionSummaryCardProps {
  eyebrow: string
  headline: string
  /** A node, not a string: the figure a note turns on is emphasized. */
  description: React.ReactNode
  /**
   * Real deadlines the section's answers just established. Left off the
   * static cards, which have no dates behind them. Rendered last, below the
   * description: the copy introduces the list ("These are your next two
   * deadlines"), so it has to be read first.
   */
  dueDates?: SummaryDueDate[]
}

/**
 * The note that closes out a completed intake section. Educational rather
 * than evaluative: it teaches something about the ground the section just
 * covered instead of grading the user's answers.
 *
 * It used to carry a `status` (covered / needs attention / not relevant), a
 * palette per status, and a checklist of per-section findings. All of that
 * implied a coverage computation the backend doesn't do yet, so the checklist
 * items were invented and the statuses were assigned by hand for QA. One moss
 * card with no inner list is the honest shape for static copy — and when real
 * per-section coverage does land, it should arrive as its own component
 * rather than by re-growing variants here.
 *
 * `dueDates` is not a step back toward that: a statutory IRS deadline is a
 * fact about the calendar, not an assessment of this practice, so naming one
 * claims nothing the intake hasn't established.
 */
export const SectionSummaryCard: React.FC<SectionSummaryCardProps> = ({
  eyebrow,
  headline,
  description,
  dueDates,
}) => (
  <div className="flex flex-col gap-4 rounded-2xl bg-moss-0 p-6">
    <div className="flex items-center gap-2">
      <Icon
        icon={faLightbulb}
        size="sm"
        color="text-moss-200"
        backgroundColor="transparent"
        backgroundClassName="bg-moss-25"
        backgroundStyle="rounded"
      />
      <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
        {eyebrow}
      </Text>
    </div>
    <Text variant={TypographyStyles.DISPLAY_SM} color={TextColors.HEADER}>
      {headline}
    </Text>
    <Text variant={TypographyStyles.BODY_MD} color={TextColors.MUTED}>
      {description}
    </Text>
    {dueDates && dueDates.length > 0 && (
      /* Tighter than the card's own rhythm: the dates are one list, not one
         more thing at the card's top level. */
      <div className="flex flex-col gap-2">
        {dueDates.map(({ label, date }) => (
          <div
            key={label}
            className="flex items-center justify-between gap-3 rounded-xl bg-neutral-0 p-3"
          >
            <Text variant={TypographyStyles.LABEL_SM}>{label}</Text>
            {/* White, not a color: these are upcoming by construction, so
                there's nothing overdue or at-risk for color to signal.
                `whitespace-nowrap` so the date does not break after its
                comma; the label beside it wraps instead. */}
            <Badge
              variant="secondary"
              color="white"
              size="sm"
              icon={faCalendar}
              className="whitespace-nowrap"
            >
              {date}
            </Badge>
          </div>
        ))}
      </div>
    )}
  </div>
)
